/**
 * The team on the other side of a record, and how to read it.
 *
 * Consumer and service owner are the two sides of every service item and every
 * change, and each side's lists name the other in a column of their own. Now
 * that the API will hand over a counterparty's team and its members, that name
 * is also a way in — see CounterpartyDrawer.
 */

/**
 * Which side of the deal a team stands on, as the API's team povs name it: a
 * consumer holds an item of one of your services, a service owner owns a
 * service you hold an item of. The two are mirror images, so a team is among
 * your consumers exactly when you are among its service owners.
 */
export type CounterpartyPov = 'consumer' | 'serviceowner';

/** as much of a team as a list record carries — enough to name it and open it */
export type CounterpartyTeam = { id?: number; name?: string } | undefined;

/**
 * The counterparty column of a list.
 *
 * `team` reads the whole team off the record rather than its name alone,
 * because the panel the name opens needs the id. `pov` says which side that
 * team stands on; without it the name stays plain text, which is what a list
 * that has no counterparty to show wants.
 */
export type PartyField = {
    label: string;
    team: (record: any) => CounterpartyTeam;
    pov?: CounterpartyPov;
};

/**
 * The side facing a reader on the given side.
 *
 * Consumer and service owner are mirror images, so the counterparty of one is
 * always the other: a page that knows which pov it is being read from knows
 * which pov to open. Lists say this in their config instead, because a list can
 * show the other side twice — the service owner's own items and the ones it
 * depends on are opposite sides of the same page.
 */
export const counterpartyOf = (readerPov: string): CounterpartyPov | undefined => {
    if (readerPov === 'consumer') return 'serviceowner';
    if (readerPov === 'serviceowner') return 'consumer';
    return undefined;
};

/** what each pov calls the team, and the resources that answer for it */
export const POV_TEAMS: Record<
    CounterpartyPov,
    { label: string; teams: string; memberships: string }
> = {
    consumer: {
        label: 'Consumer',
        teams: 'consumer_teams',
        memberships: 'consumer_team_memberships',
    },
    serviceowner: {
        label: 'Service Owner',
        teams: 'serviceowner_teams',
        memberships: 'serviceowner_team_memberships',
    },
};
